<template>
	<div
		class="v-cover"
		:class="{
			'v-cover--overlay': overlay,
			'v-cover--contain': contain,
			'v-cover--dark': dark
		}"
	>
		<div
			v-if="image"
			class="v-cover__image"
			:style="{ backgroundImage: `url('${image}')` }"
			ref="image"
		></div>

		<p class="wrapper v-cover__wrapper v-cover__title">
			{{ title }}
		</p>

		<div
			v-for="(item, index) in description"
			:key="index"
			class="wrapper v-cover__wrapper v-cover__description"
		>
			{{ item?.description }}
		</div>

		<div v-if="cta" class="v-cover__content wrapper">
			<v-button class="v-cover__button" v-touch:tap="() => this.start()">
				<p class="v-cover__button-text">{{ cta }}</p>
			</v-button>
		</div>
		<slot />
	</div>
</template>

<style lang="sass">
.v-cover
  overflow: hidden
  height: 100vh
  color: var(--white)
  display: flex
  flex-direction: column
  align-items: stretch
  justify-content: center
  position: relative
  overflow-wrap: break-word
  font:
    family: var(--font-family-title)
    size: var(--font-size-large)
  text:
    align: center
    transform: uppercase
    decoration: none

  &__image
    width: 100%
    height: 130%
    top: 50%
    left: 0
    position: absolute
    z-index: 0
    transform: translateY(-50%)
    background:
      repeat: no-repeat
      size: cover
      position: center center

  &__content
    line-height: var(--font-height-text)
    margin:
      top: 30px
      bottom: 30px
    text-align: center

  &__title
    z-index: 1
    padding-bottom: 2rem
    font-size: var(--font-size-cover)

  &__description
    z-index: 1
    line-height: 1.5rem
    font:
      family: var(--font-family-label)
      size: var(--font-size-tiny)

  &__button
    max-width: 570px
    margin-left: auto
    margin-right: auto
    .v-button__label
      padding: 10px
      line-height: 24px
      letter-spacing: 1px
      font-size: var(--font-size-book)
      font-weight: 100

  &__button-text
    color: var(--grey)
    font:
      family: var(--font-family-label)
      size: var(--font-size-tiny)

  &__wrapper
    word-break: break-word

  &--overlay &__image::before
    content: ''
    background-color: rgba(0, 0, 0, 0.6)
    width: 100%
    height: 100%
    top: 0
    left: 0
    position: absolute

  &--contain &__image
    background-size: contain

  &--dark
    color: var(--black)

  @media screen and (max-width: 64em)
    &__title
      padding: 0 2.5rem 2.5rem 2.5rem
      font-size: var(--font-size-mobile-cover)

    &__description
      line-height: 1rem
      font:
        size: var(--font-size-mini)

    &__button-title
      font:
        size: var(--font-size-mini)

    &__button-text
      font:
        size: var(--font-size-mini)

    &--contain &__image
      width: 150%
      left: -20%
</style>

<script>
import VButton from '@/components/VButton.vue'

export default {
	name: 'VCover',

	props: {
		image: {
			type: String,
			default: ''
		},
		overlay: {
			type: Boolean,
			default: false
		},
		contain: {
			type: Boolean,
			default: false
		},
		dark: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: null
		},
		description: {
			type: Object,
			default: () => {}
		},
		cta: {
			type: String,
			default: null
		}
	},

	watch: {
		image: {
			imediate: true,
			handler(image) {
				cancelAnimationFrame(this.frameRequest)
				image && this.updateParallax()
			}
		}
	},

	created() {
		this.updateParallax = this.updateParallax.bind(this)
		this.onResize = this.onResize.bind(this)
		this.rect = {}
		this.frameRequest = null
	},

	mounted() {
		addEventListener('resize', this.onResize)
	},

	beforeDestroy() {
		cancelAnimationFrame(this.frameRequest)
		removeEventListener('resize', this.onResize)
	},

	methods: {
		start() {
			this.$audio.play('/sounds/ding.mp3')
			this.$router.push({ name: 'question', params: { id: 'A' } })
			this.$ga.send('skin_concierge', {
				skin_concierge_action: 'start'
			})
		},

		updateParallax() {
			const rect = this.$el.getBoundingClientRect()

			if (
				rect.top !== this.rect.top ||
				rect.height !== this.rect.height
			) {
				const imageRect = this.$refs.image.getBoundingClientRect()
				const viewport = window.innerHeight
				const offset =
					((imageRect.height - rect.height) / imageRect.height) * 100

				this.rect = rect
				this.$refs.image.style.transform = `translateY(${
					offset *
					(1 -
						Math.min(
							Math.max((rect.top + rect.height) / rect.height, 0),
							1
						) -
						Math.min(Math.max(rect.top / viewport, 0), 1))
				}%)`
			}

			this.frameRequest = requestAnimationFrame(this.updateParallax)
		},

		onResize() {
			this.rect = {}
		}
	},

	components: {
		VButton
	}
}
</script>
